import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api/endpoints';
import type { PairingCode, Station, StationType } from '../api/types';
import { usePoll } from '../hooks/usePoll';
import {
  formatCountdown,
  formatDateTime,
  STATION_TYPE_LABEL,
} from '../lib/format';
import { ShutdownDialog } from '../components/ShutdownDialog';
import { TvLockScreenCard } from '../components/TvLockScreenCard';
import { useToast } from '../components/Toast';
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Input,
  Modal,
  Select,
  Spinner,
} from '../components/ui';

// Codes are single-use and lapse after 15 minutes, so the admin needs to see
// how long they have while walking to the machine.
function PairingCodeDialog({
  station,
  onClose,
}: {
  station: Station;
  onClose: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState<PairingCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const issue = async () => {
    setError(null);
    try {
      const result = await api.stations.pairingCode(station.station_id);
      setCode(result);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    void issue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!code) return;
    const tick = () => {
      const remaining =
        (new Date(code.pairing_code_expires_at).getTime() - Date.now()) / 1000;
      setSecondsLeft(Math.max(0, remaining));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [code]);

  const expired = code !== null && secondsLeft <= 0;
  // A PS5's agent is the HHG Station app on the TV the console is plugged
  // into, so staff walk to the TV, not the console.
  const isPs5 = station.station_type === 'ps5';

  return (
    <Modal
      title={isPs5 ? `Pair the TV for ${station.name}` : `Pair the agent on ${station.name}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        {!code ? (
          <Spinner label="Generating a code…" />
        ) : (
          <>
            <div className="rounded-xl border border-slate-800 bg-slate-950 p-6 text-center">
              <p className="font-mono text-4xl font-bold tracking-[0.3em] text-sky-300">
                {code.pairing_code}
              </p>
              <p className="mt-2 text-sm text-slate-400">
                {expired ? 'This code has expired.' : `Expires in ${formatCountdown(secondsLeft)}`}
              </p>
            </div>

            <p className="text-sm text-slate-400">
              {isPs5
                ? `Type this on the TV ${station.name} is plugged into, in the HHG Station app. `
                : `Type this into the HHG agent running on ${station.name}. `}
              The code works once. The station shows as paired on the next floor
              refresh.
            </p>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="secondary" className="flex-1" onClick={issue}>
                Generate a new code
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  toast.info('Close this once the agent reports in.');
                  onClose();
                }}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function StationFormDialog({
  station,
  onClose,
  onSaved,
}: {
  station: Station | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(station?.name ?? '');
  const [type, setType] = useState<StationType>(station?.station_type ?? 'pc_standard');
  const [order, setOrder] = useState(String(station?.display_order ?? 0));
  const [mac, setMac] = useState(station?.mac_address ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (station) {
        await api.stations.update(station.station_id, {
          name: name.trim(),
          display_order: Number(order),
          mac_address: mac.trim() || undefined,
        });
        toast.success('Station updated.');
      } else {
        await api.stations.create({
          name: name.trim(),
          station_type: type,
          display_order: Number(order),
          mac_address: mac.trim() || undefined,
        });
        toast.success('Station created.');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={station ? `Edit ${station.name}` : 'New station'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          autoFocus
        />
        {!station && (
          <Select
            label="Type"
            value={type}
            onChange={(event) => setType(event.target.value as StationType)}
          >
            <option value="pc_standard">Gaming PC</option>
            <option value="pc_simulator">Driving simulator</option>
            <option value="ps5">PS5</option>
          </Select>
        )}
        <Input
          label="Display order"
          value={order}
          onChange={(event) => setOrder(event.target.value)}
          inputMode="numeric"
          hint="Lower numbers sit earlier on the floor plan."
        />
        <Input
          label="MAC address (optional)"
          value={mac}
          onChange={(event) => setMac(event.target.value)}
          placeholder="AA:BB:CC:DD:EE:FF"
          hint="Recorded now so Wake-on-LAN can be enabled later without re-visiting the machine."
        />
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Saving…' : 'Save station'}
        </Button>
      </form>
    </Modal>
  );
}

// The simulator rig doubles as a standard PC when the wheel is off. The type
// decides which wallet pool pays, so the switch says exactly that, and the
// backend refuses it while anyone is on the seat.
function SwitchTypeDialog({
  station,
  onClose,
  onDone,
}: {
  station: Station;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const target = station.station_type === 'pc_simulator' ? 'pc_standard' : 'pc_simulator';
  const targetPool = target === 'pc_simulator' ? 'driving simulator' : 'PC gaming';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.stations.changeType(station.station_id, target);
      toast.success(
        `${result.name} is now ${STATION_TYPE_LABEL[result.station_type]}. New sessions on it are paid from ${targetPool} time.`,
      );
      onDone();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Switch ${station.name} to ${STATION_TYPE_LABEL[target]}`} onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {station.active_session ? (
          <ErrorNote>
            {station.active_session.customer_name} is on this station. Stop their
            session before switching it.
          </ErrorNote>
        ) : (
          <p className="text-sm text-slate-300">
            From now on, customers seated here pay from their{' '}
            <span className="font-semibold text-slate-100">{targetPool}</span> time.
            Past sessions keep the pool they were paid from. You can switch it
            back at any time the seat is free.
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            className="flex-1"
            disabled={busy || Boolean(station.active_session)}
            onClick={submit}
          >
            {busy ? 'Switching…' : `Switch to ${STATION_TYPE_LABEL[target]}`}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// The "delete". Retiring keeps the row so past sessions and Reports still name
// the seat; it leaves the floor and is unpaired. Typing the name is the guard,
// because on a phone a stray tap here would otherwise empty a seat.
function RetireDialog({
  station,
  onClose,
  onDone,
}: {
  station: Station;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim().toLowerCase() === station.name.toLowerCase();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await api.stations.retire(station.station_id);
      toast.success(`${station.name} retired. Restore it any time from Retired stations.`);
      onDone();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Retire ${station.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {station.active_session ? (
          <ErrorNote>
            {station.active_session.customer_name} is on this station. Stop their
            session before retiring it.
          </ErrorNote>
        ) : (
          <div className="space-y-2 text-sm text-slate-300">
            <p>
              {station.name} leaves the floor and the station counts, and its{' '}
              {station.station_type === 'ps5' ? 'TV' : 'agent'} is unpaired.
            </p>
            <p className="text-slate-400">
              Past sessions, orders and reports keep it. You can restore it later
              from Retired stations, then pair it again.
            </p>
          </div>
        )}
        <Input
          label={`Type ${station.name} to confirm`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoFocus
          disabled={Boolean(station.active_session)}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="submit"
            variant="danger"
            className="flex-1"
            disabled={busy || !matches || Boolean(station.active_session)}
          >
            {busy ? 'Retiring…' : `Retire ${station.name}`}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// The end of the line for a retired seat — no restore after this. The backend
// keeps a seat with history in the database so reports still name it, but it
// is gone from every screen and its name is free again.
function DeleteStationDialog({
  station,
  onClose,
  onDone,
}: {
  station: Station;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim().toLowerCase() === station.name.toLowerCase();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await api.stations.remove(station.station_id);
      toast.success(`${station.name} deleted.`);
      onDone();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Delete ${station.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="space-y-2 text-sm text-slate-300">
          <p>
            {station.name} is removed for good and cannot be restored. Its name
            can be used for a new station.
          </p>
          <p className="text-slate-400">
            Past sessions and orders on it stay in reports.
          </p>
        </div>
        <Input
          label={`Type ${station.name} to confirm`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoFocus
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="submit"
            variant="danger"
            className="flex-1"
            disabled={busy || !matches}
          >
            {busy ? 'Deleting…' : `Delete ${station.name}`}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function StationsAdminPage() {
  const toast = useToast();
  const { data, error, loading, refresh } = usePoll(
    (signal) => api.stations.list(signal, true),
    10000,
  );

  const [editing, setEditing] = useState<Station | null>(null);
  const [creating, setCreating] = useState(false);
  const [pairing, setPairing] = useState<Station | null>(null);
  const [switching, setSwitching] = useState<Station | null>(null);
  const [shuttingDown, setShuttingDown] = useState<Station | null>(null);
  const [retiring, setRetiring] = useState<Station | null>(null);
  const [deleting, setDeleting] = useState<Station | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const inService = data?.filter((station) => !station.retired_at) ?? [];
  const retired = data?.filter((station) => station.retired_at) ?? [];

  const restore = async (station: Station) => {
    try {
      await api.stations.restore(station.station_id);
      toast.success(
        station.station_type === 'ps5'
          ? `${station.name} is back on the floor.`
          : `${station.name} is back on the floor. Pair its agent before seating anyone.`,
      );
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const setStatus = async (station: Station, status: 'idle' | 'maintenance') => {
    try {
      await api.stations.update(station.station_id, { status });
      toast.success(
        status === 'maintenance'
          ? `${station.name} is out of service.`
          : `${station.name} is back in service.`,
      );
      refresh();
    } catch (err) {
      // A station with a running session cannot be pulled out from under the
      // customer sitting at it — the backend says so in plain words.
      toast.error((err as Error).message);
    }
  };

  const unpair = async (station: Station) => {
    try {
      await api.stations.unpair(station.station_id);
      toast.success(`${station.name} unpaired. Its agent token is revoked.`);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Stations</h1>
        <Button onClick={() => setCreating(true)}>New station</Button>
      </div>

      <TvLockScreenCard />

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          {inService.map((station) => {
            const isPs5 = station.station_type === 'ps5';
            return (
              <Card key={station.station_id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-100">
                      {station.name}{' '}
                      <span className="text-xs font-normal text-slate-500">
                        #{station.display_order}
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {STATION_TYPE_LABEL[station.station_type]}
                      {station.hostname && ` · ${station.hostname}`}
                      {station.ip_address && ` · ${station.ip_address}`}
                      {station.mac_address && ` · ${station.mac_address}`}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge
                        tone={
                          station.status === 'active'
                            ? 'green'
                            : station.status === 'maintenance'
                              ? 'red'
                              : 'slate'
                        }
                      >
                        {station.status}
                      </Badge>
                      {/* A PS5's agent is the app on its TV. An unpaired PS5 is
                          timer-only, as before. */}
                      {station.agent.paired ? (
                        <Badge tone={station.agent.online ? 'green' : 'amber'}>
                          {isPs5
                            ? station.agent.online ? 'TV online' : 'TV offline'
                            : station.agent.online ? 'Agent online' : 'Agent offline'}
                        </Badge>
                      ) : (
                        <Badge tone="slate">{isPs5 ? 'No TV paired · timer only' : 'Not paired'}</Badge>
                      )}
                      {station.shutdown_requested_at && (
                        <Badge tone="amber">Shutting down</Badge>
                      )}
                      {station.agent.paired && (
                        <span className="text-xs text-slate-600">
                          last seen {formatDateTime(station.agent.last_heartbeat_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={() => setEditing(station)}>
                      Edit
                    </Button>
                    <Button variant="secondary" onClick={() => setPairing(station)}>
                      {isPs5
                        ? station.agent.paired ? 'Re-pair TV' : 'Pair TV'
                        : station.agent.paired ? 'Re-pair agent' : 'Pair agent'}
                    </Button>
                    {station.agent.paired && (
                      <Button variant="ghost" onClick={() => unpair(station)}>
                        Unpair
                      </Button>
                    )}
                    {!isPs5 && (
                      <Button variant="secondary" onClick={() => setSwitching(station)}>
                        {station.station_type === 'pc_simulator'
                          ? 'Use as Gaming PC'
                          : 'Use as Simulator'}
                      </Button>
                    )}
                    {!isPs5 && station.agent.paired && (
                      <Button variant="ghost" onClick={() => setShuttingDown(station)}>
                        Shut down
                      </Button>
                    )}
                    {station.status === 'maintenance' ? (
                      <Button variant="ghost" onClick={() => setStatus(station, 'idle')}>
                        Back in service
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => setStatus(station, 'maintenance')}
                      >
                        Maintenance
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => setRetiring(station)}>
                      Retire
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}

          {retired.length > 0 && (
            <div className="space-y-3 pt-2">
              <Button
                variant="ghost"
                className="w-full justify-between"
                aria-expanded={showRetired}
                onClick={() => setShowRetired((open) => !open)}
              >
                <span>Retired stations ({retired.length})</span>
                <span aria-hidden>{showRetired ? '−' : '+'}</span>
              </Button>
              {showRetired &&
                retired.map((station) => (
                  <Card key={station.station_id}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-400">{station.name}</p>
                        <p className="text-xs text-slate-500">
                          {STATION_TYPE_LABEL[station.station_type]} · retired{' '}
                          {formatDateTime(station.retired_at)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="secondary" onClick={() => restore(station)}>
                          Restore
                        </Button>
                        <Button variant="ghost" onClick={() => setDeleting(station)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
            </div>
          )}
        </div>
      )}

      {(creating || editing) && (
        <StationFormDialog
          station={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={refresh}
        />
      )}
      {pairing && (
        <PairingCodeDialog station={pairing} onClose={() => setPairing(null)} />
      )}
      {switching && (
        <SwitchTypeDialog
          station={switching}
          onClose={() => setSwitching(null)}
          onDone={refresh}
        />
      )}
      {retiring && (
        <RetireDialog
          station={retiring}
          onClose={() => setRetiring(null)}
          onDone={refresh}
        />
      )}
      {deleting && (
        <DeleteStationDialog
          station={deleting}
          onClose={() => setDeleting(null)}
          onDone={refresh}
        />
      )}
      {shuttingDown && (
        <ShutdownDialog
          station={shuttingDown}
          onClose={() => setShuttingDown(null)}
          onDone={refresh}
        />
      )}
    </div>
  );
}
